import { Controller, useForm } from 'react-hook-form';
import {
    Stack,
    FormControl,
    InputGroup,
    FormLabel,
    InputLeftElement,
    Input,
    FormErrorMessage,
    Select,
    SimpleGrid,
    Flex
} from '@chakra-ui/react';
import {
    BiDollar,
    BiCalendar
} from 'react-icons/bi';
import {
    FaRegClone
} from 'react-icons/fa';
import { getFilteredObject } from 'utils/object';
import DimensionsInput from '../../Input/DimensionsInput/DimensionsInput';
import MaterialTable from '../../Material/MaterialTable/MaterialTable';
import ActionButton from '../../Button/ActionButton';
import { ListingModel, MaterialModel } from 'models';
import { MeasurementData } from 'models/types';
import { ListingType } from 'models/Listing/types';
import BasicListingCard from 'components/Listing/BasicListingCard/BasicListingCard';

export type ListingEditFormOption = 'name' | 'rawPrice' | 'date' | 'edition' | 'dimensions' | 'estimateLow' | 'estimateHigh' | 'materialIds' | 'type';

export interface ListingEditFormProps {
    listing?: ListingModel;
    materialList?: MaterialModel[];
    defaultFormData?: ListingEditFormData;
    onSubmit?: (data: ListingEditFormData) => void;
    submitLoading?: boolean;
    displayOptions?: ListingEditFormOption[];
    requiredOptions?: ListingEditFormOption[];
    disabledOptions?: ListingEditFormOption[];
}

export interface ListingEditFormData {
    name?: string;
    rawPrice?: number;
    date?: Date | string | undefined;
    edition?: string;
    dimensions?: MeasurementData;
    estimateLow?: number;
    estimateHigh?: number;
    materialIds?: string[];
    type?: ListingType;
}

export default function ListingEditForm({
    listing,
    materialList,
    defaultFormData,
    onSubmit,
    submitLoading,
    displayOptions,
    requiredOptions = [],
    disabledOptions = []
}: ListingEditFormProps) {
    const {
        control,
        handleSubmit,
        register,
        watch,
        setValue,
        formState: { errors, isDirty, dirtyFields },
    } = useForm<ListingEditFormData>({ defaultValues: defaultFormData });

    const formDisplayOptions: Record<string, boolean> = (
        displayOptions || ['name', 'rawPrice', 'date', 'edition', 'dimensions', 'estimateLow', 'estimateHigh', 'materialIds', 'type']
    ).reduce((acc: Record<string, boolean>, curr: ListingEditFormOption) => (acc[curr] = true, acc), {});

    const formDisabledOptions: Record<string, boolean> = disabledOptions.reduce((acc: Record<string, boolean>, curr: ListingEditFormOption) => (acc[curr] = true, acc), {});

    const formRequiredOptions: Record<string, boolean> = requiredOptions.reduce((acc: Record<string, boolean>, curr: ListingEditFormOption) => (acc[curr] = true, acc), {});

    const onFormSubmit = (data: ListingEditFormData) => {
        const changedData = getFilteredObject(Object.keys(dirtyFields), data as Record<string, unknown>);
        onSubmit?.(changedData);
    }

    function renderNameInput() {
        const shouldDisplay = !!formDisplayOptions['name'];
        const isRequired = !!formRequiredOptions['name'];
        const isDisabled = !!formDisabledOptions['name'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.name} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="name" fontSize="sm">Listing Name</FormLabel>
                <Input
                    id="name"
                    placeholder="Enter listing name"
                    {...register('name', { required: isRequired ? 'Name is required' : false })}
                />
                <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
            </FormControl>
        );
    }

    function renderPriceInput() {
        const shouldDisplay = !!formDisplayOptions['rawPrice'];
        const isRequired = !!formRequiredOptions['rawPrice'];
        const isDisabled = !!formDisabledOptions['rawPrice'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.rawPrice} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="rawPrice" fontSize="sm">Sale Price</FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <BiDollar />
                    </InputLeftElement>
                    <Input
                        id="rawPrice"
                        type="number"
                        step="0.01"
                        placeholder="Enter sale price"
                        {...register('rawPrice', {
                            required: isRequired ? 'Price is required' : false,
                            valueAsNumber: true,
                            min: { value: 0, message: 'Price must be positive' }
                        })}
                    />
                </InputGroup>
                <FormErrorMessage>{errors.rawPrice && errors.rawPrice.message}</FormErrorMessage>
            </FormControl>
        );
    }

    function renderDateInput() {
        const shouldDisplay = !!formDisplayOptions['date'];
        const isRequired = !!formRequiredOptions['date'];
        const isDisabled = !!formDisabledOptions['date'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.date} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="date" fontSize="sm">Sale Date</FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <BiCalendar />
                    </InputLeftElement>
                    <Input
                        id="date"
                        type="date"
                        {...register('date', { required: isRequired ? 'Date is required' : false })}
                    />
                </InputGroup>
                <FormErrorMessage>{errors.date && errors.date.message}</FormErrorMessage>
            </FormControl>
        );
    }

    function renderEditionInput() {
        const shouldDisplay = !!formDisplayOptions['edition'];
        const isRequired = !!formRequiredOptions['edition'];
        const isDisabled = !!formDisabledOptions['edition'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.edition} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="edition" fontSize="sm">Edition</FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <FaRegClone />
                    </InputLeftElement>
                    <Input
                        id="edition"
                        placeholder="e.g., 1/100, A.P., H.C."
                        {...register('edition', { required: isRequired ? 'Edition is required' : false })}
                    />
                </InputGroup>
                <FormErrorMessage>{errors.edition && errors.edition.message}</FormErrorMessage>
            </FormControl>
        );
    }

    function renderDimensionsInput() {
        const shouldDisplay = !!formDisplayOptions['dimensions'];
        const isRequired = !!formRequiredOptions['dimensions'];
        const isDisabled = !!formDisabledOptions['dimensions'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.dimensions} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="dimensions" fontSize="sm">Dimensions</FormLabel>
                <Controller
                    control={control}
                    name="dimensions"
                    rules={{ required: isRequired ? 'Dimensions are required' : false }}
                    render={({ field: { onChange, value } }) => (
                        <DimensionsInput
                            defaultData={defaultFormData?.dimensions}
                            onChange={onChange}
                        />
                    )}
                />
                <FormErrorMessage>{errors.dimensions && errors.dimensions.message}</FormErrorMessage>
            </FormControl>
        );
    }

    function renderEstimateInputs() {
        const shouldDisplayLow = !!formDisplayOptions['estimateLow'];
        const shouldDisplayHigh = !!formDisplayOptions['estimateHigh'];
        const isRequiredLow = !!formRequiredOptions['estimateLow'];
        const isRequiredHigh = !!formRequiredOptions['estimateHigh'];
        const isDisabledLow = !!formDisabledOptions['estimateLow'];
        const isDisabledHigh = !!formDisabledOptions['estimateHigh'];

        const shouldDisplay = shouldDisplayLow || shouldDisplayHigh;

        return shouldDisplay && (
            <FormControl>
                <FormLabel fontSize="sm">Estimate</FormLabel>
                <SimpleGrid columns={2} spacing={4}>
                    {shouldDisplayLow && (
                        <FormControl isInvalid={!!errors.estimateLow} isRequired={isRequiredLow} isDisabled={isDisabledLow}>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <BiDollar />
                                </InputLeftElement>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="Low"
                                    {...register('estimateLow', {
                                        required: isRequiredLow ? 'Low estimate is required' : false,
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Must be positive' }
                                    })}
                                />
                            </InputGroup>
                            <FormErrorMessage>{errors.estimateLow && errors.estimateLow.message}</FormErrorMessage>
                        </FormControl>
                    )}
                    {shouldDisplayHigh && (
                        <FormControl isInvalid={!!errors.estimateHigh} isRequired={isRequiredHigh} isDisabled={isDisabledHigh}>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <BiDollar />
                                </InputLeftElement>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="High"
                                    {...register('estimateHigh', {
                                        required: isRequiredHigh ? 'High estimate is required' : false,
                                        valueAsNumber: true,
                                        min: { value: 0, message: 'Must be positive' }
                                    })}
                                />
                            </InputGroup>
                            <FormErrorMessage>{errors.estimateHigh && errors.estimateHigh.message}</FormErrorMessage>
                        </FormControl>
                    )}
                </SimpleGrid>
            </FormControl>
        );
    }

    function renderMaterialsTable() {
        const shouldDisplay = !!formDisplayOptions['materialIds'] && (materialList || []).length > 0;
        const isRequired = !!formRequiredOptions['materialIds'];
        const isDisabled = !!formDisabledOptions['materialIds'];

        return shouldDisplay && (
            <FormControl isDisabled={isDisabled} isRequired={isRequired} isInvalid={!!errors.materialIds}>
                <FormLabel htmlFor="materialIds" fontSize="sm">Materials</FormLabel>
                <Controller
                    control={control}
                    name="materialIds"
                    rules={{
                        required: isRequired,
                        validate: (values) => (!isRequired || !!values?.length) || 'Material must be selected'
                    }}
                    render={() => (
                        <MaterialTable
                            materials={materialList || []}
                            selectedMaterialIds={watch('materialIds')}
                            onSelect={(materials) => setValue('materialIds', materials, { shouldDirty: true })}
                        />
                    )} />
                <FormErrorMessage>
                    {errors.materialIds && errors.materialIds.message}
                </FormErrorMessage>
            </FormControl>
        );
    }

    function renderTypeDropdown() {
        const shouldDisplay = !!formDisplayOptions['type'];
        const isRequired = !!formRequiredOptions['type'];
        const isDisabled = !!formDisabledOptions['type'];

        return shouldDisplay && (
            <FormControl isInvalid={!!errors.type} isRequired={isRequired} isDisabled={isDisabled}>
                <FormLabel htmlFor="type" fontSize="sm">Type</FormLabel>
                <Select
                    id="type"
                    placeholder="Select type"
                    {...register('type', { required: isRequired ? 'Type is required' : false })}
                >
                    {Object.entries(ListingType).map(([key, value]) => (
                        <option key={key} value={value}>
                            {value}
                        </option>
                    ))}
                </Select>
                <FormErrorMessage>{errors.type && errors.type.message}</FormErrorMessage>
            </FormControl>
        );
    }

    return (
        <form onSubmit={handleSubmit(onFormSubmit)}>
            <Stack spacing={4}>
                {!!listing && <BasicListingCard listing={listing} />}
                {renderNameInput()}
                {renderPriceInput()}
                {renderDateInput()}
                {renderEstimateInputs()}
                {renderEditionInput()}
                {renderDimensionsInput()}
                {renderMaterialsTable()}
                {renderTypeDropdown()}

                <Flex justifyContent="center">
                    <ActionButton
                        type="submit"
                        text="Submit"
                        disabled={!isDirty || submitLoading}
                        loading={submitLoading}
                    />
                </Flex>
            </Stack>
        </form>
    );
}

